import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { phase2Service } from '@/core/services/phase2';
import { practiceService } from '@/core/services/practice';
import { reportOptionsService } from '@/core/services/reportOptions';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import {
  STATUTORY_STATES,
  type DisclosureAnswerInput,
  type DisclosureFieldView,
  type DisclosureSectionView,
  type StatutoryDatabaseScreen,
  type StatutoryFieldRow,
  type StatutorySection,
} from '@/core/models/phase2.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { DateField } from '@/kit/DateField';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { Tooltip } from '@/kit/Tooltip';
import { NO_PERIOD_PLACEHOLDER } from '@/core/format/periodLabel';
import {
  NoPeriodNote,
  SearchField,
  fixUrl,
  numberFromInput,
  periodTo,
  useAlive,
  useClientInit,
  useLive,
} from './common';
import { describeFramework, taxonomyDisplayName } from './frameworkLabel';
import './TaxreturnStatutorydatabase.scss';

const OVERRIDE_STATES = STATUTORY_STATES.filter((s) => s.id >= 0 && s.id <= 2);

/** APT250: a Yes/No field is a Yes / No choice, so "not answered yet" is not mistaken for "No". */
const YES_NO = [
  { id: 'yes', name: 'Yes' },
  { id: 'no', name: 'No' },
];

/** APT250: shown when the API gives no reason for a locked period. */
export const LOCKED_FALLBACK = 'This period is locked, so its disclosures are read-only.';

/** Colours the state the way the rest of the app does: green settled, red outstanding. */
/**
 * task-133: an override is shown as the state it puts in force - Optional, Required or Not applicable - and
 * coloured and iconned like any other row in that state. It used to keep its own blue gavel "Overridden" look,
 * so a disclosure overridden to Optional still read as overridden rather than optional. The "Overridden" line
 * under the state and the "Overridden to ..." reason still say that it was overridden.
 */
function shownState(field: StatutoryFieldRow): number {
  if (field.state === 4) return 4;
  return field.override ? field.effectiveState : field.state;
}

function stateClass(field: StatutoryFieldRow): string {
  const state = shownState(field);
  if (state === 4) return 'text-red';
  if (state === 0) return 'text-gray';
  return field.answer || field.hasValue ? 'text-green' : 'text-gray';
}

function stateIcon(field: StatutoryFieldRow): string {
  const state = shownState(field);
  if (state === 4) return 'error_outline';
  if (state === 0) return 'remove_circle_outline';
  return field.answer || field.hasValue ? 'check_circle' : 'radio_button_unchecked';
}

/** APT250: the field holds an answer recorded on this screen (not one a Settings screen gives it). */
const hasOwnAnswer = (field: StatutoryFieldRow) => !!field.answer && !field.answerSource;

/** APT250: shown when a typed date cannot be read, so the answer is not saved. */
export const INVALID_DATE = 'Enter a valid date (dd/mm/yyyy).';

/** APT250: shown when the amount box holds something that is not a number (a comma, say). */
export const INVALID_AMOUNT = 'Enter the amount as a number, without commas (for example 1000.50).';

/**
 * APT250: what the Answer column says. A field answered on a Settings screen used to show "-" although the screen
 * counted it as answered; it now shows that screen's answer, or "Answered" when the screen holds a note rather than a
 * short value (an accounting policy, say).
 */
function shownAnswer(field: StatutoryFieldRow): string {
  if (field.answer) return field.answer;
  return field.hasValue && field.answerSource ? 'Answered' : '-';
}

interface Model {
  periods: PeriodRow[];
  /** APT355: the client's periods have been read (so "no accounting period" is only said once it is known). */
  periodsLoaded: boolean;
  periodId: string;
  searchText: string;
  stateFilter: number;
  screen: StatutoryDatabaseScreen | null;
  sections: StatutorySection[];
  /** APT362: the company's saved taxonomy (Report Settings > Company Info) and its name, so the framework is named the way the settings name it. Null when it could not be read. */
  taxonomyCode: string | null;
  taxonomyName: string | null;
  /** APT250/AP-008: the section shown below the tab strip (`sectionCode`), one section's disclosures at a time. */
  activeSection: string;
  /** The field whose override panel is open, and the values being entered. */
  overrideField: StatutoryFieldRow | null;
  overrideState: number;
  overrideReason: string;
  /** Apply was pressed with the reason empty (draws the red outline). */
  overrideTried: boolean;
  /** An override is being saved (stops a second click). */
  isOverrideSaving: boolean;
  /**
   * ap5-a6 (item 134) / APT250: the row being answered, in its own Answer cell. The statutory screen only knows a
   * field's `fieldCode`, but the disclosure API keys answers by `fieldId`, so the section is fetched when the editor
   * opens - that also brings the choice list for Choice fields and the ETag the save needs.
   */
  answerField: StatutoryFieldRow | null;
  answerDefinition: DisclosureFieldView | null;
  answerSection: DisclosureSectionView | null;
  answerText: string;
  answerDecimal: number | null;
  /** The amount as typed ("1." is not yet the number 1). */
  answerAmountText: string;
  /** yyyy-mm-dd, or '' when blank. */
  answerDate: string;
  /** APT250: null until Yes or No is chosen. */
  answerBoolean: boolean | null;
  answerChoice: string | null;
  /** APT250: typed date text the date field could not read (null while the date is valid or empty). */
  answerDateInvalid: string | null;
  /** APT250: the amount box holds something the browser cannot read as a number. */
  answerAmountInvalid: boolean;
  isAnswerLoading: boolean;
  isAnswerSaving: boolean;
}

const blankAnswer = {
  answerText: '',
  answerDecimal: null,
  answerAmountText: '',
  answerDate: '',
  answerBoolean: null,
  answerChoice: null,
  answerDateInvalid: null,
  answerAmountInvalid: false,
} as const;

/**
 * The statutory database (AP-008): a searchable disclosure tree showing each field's state (Required / Optional /
 * Not applicable / Overridden / Error), the rule id behind it, why it is required, what triggered it, and a deep link
 * to the screen that fixes it. An authorised reviewer can override a state with a reason; the tree is re-resolved as
 * soon as the override is recorded. (Angular TaxreturnStatutorydatabaseComponent.)
 *
 * APT250: every disclosure that is not Not applicable is answered in its own row - an always-visible Answer button
 * opens an editor in the Answer cell that fits the field (text, amount, date, Yes / No, or the field's choices) - and
 * the saved answer, or the Settings screen's, is shown there. A locked period is read-only and says why.
 *
 * APT250 (review): a disclosure a Settings screen answers (country of incorporation from Company Information, and so on)
 * is printed from that screen, not from an answer here, so it is not answered here: the row shows that screen's answer
 * with "From <screen>" and its Change link opens that screen. It used to open an editor filled in from the Settings
 * value, and saving made a copy here the accounts never printed, which then hid the Settings answer.
 */
export function TaxreturnStatutorydatabase() {
  const alive = useAlive();
  const navigate = useNavigate();
  // #173: "Resolve" on a submission's disclosure issue opens this screen on that period, searched to that field.
  const [searchParams] = useSearchParams();
  const [m, set, ref] = useLive<Model>({
    periods: [],
    periodsLoaded: false,
    periodId: '',
    searchText: searchParams.get('field') ?? '',
    stateFilter: -1,
    screen: null,
    sections: [],
    taxonomyCode: null,
    taxonomyName: null,
    activeSection: '',
    overrideField: null,
    overrideState: 1,
    overrideReason: '',
    overrideTried: false,
    isOverrideSaving: false,
    answerField: null,
    answerDefinition: null,
    answerSection: null,
    ...blankAnswer,
    isAnswerLoading: false,
    isAnswerSaving: false,
  });

  const load = async () => {
    const s = ref.current;
    if (!s.periodId) {
      return;
    }
    try {
      const screen = await phase2Service.getStatutoryDatabase(s.periodId, s.searchText, s.stateFilter);
      if (alive.current) bind(screen);
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const bind = (screen: StatutoryDatabaseScreen) => {
    const sections = screen.sections ?? [];
    // AP-008: keep the tab the preparer is on if it still exists after the reload, else land on the first section.
    const activeSection = sections.some((section) => section.sectionCode === ref.current.activeSection)
      ? ref.current.activeSection
      : (sections[0]?.sectionCode ?? '');
    set({ screen, sections, activeSection });
    // APT250: a period that has become locked cannot be answered; drop an open editor.
    if (screen.isLocked && ref.current.answerField) cancelAnswer();
  };

  const tabStripRef = useRef<HTMLDivElement>(null);
  const [tabScroll, setTabScroll] = useState({ canLeft: false, canRight: false });

  const updateTabScroll = () => {
    const el = tabStripRef.current;
    if (!el) return;
    setTabScroll({ canLeft: el.scrollLeft > 2, canRight: el.scrollLeft < el.scrollWidth - el.clientWidth - 2 });
  };

  useEffect(() => {
    updateTabScroll();
    window.addEventListener('resize', updateTabScroll);
    return () => window.removeEventListener('resize', updateTabScroll);
  }, [m.sections]);

  const scrollTabs = (delta: number) => tabStripRef.current?.scrollBy({ left: delta, behavior: 'smooth' });

  /**
   * AP-008: switching tabs only changes which section's table is shown - it does not touch an open answer editor or
   * override panel, which belong to a field rather than a section and simply stop being visible until its tab is
   * shown again (the override panel, outside any section's table, stays visible across every tab regardless).
   */
  const selectSection = (sectionCode: string) => {
    if (sectionCode !== ref.current.activeSection) set({ activeSection: sectionCode });
  };

  // Search typing is debounced by 400 ms; the selects reload at once.
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );
  const onSearchChange = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      searchTimer.current = null;
      void load();
    }, 400);
  };

  useClientInit(async (client) => {
    try {
      const result = await practiceService.getClientPeriods(client.id);
      if (!alive.current) return;
      const periods = result.periods ?? [];
      // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
      const requested = searchParams.get('periodId');
      const periodId =
        periods.find((p) => p.id === requested)?.id ?? (await resolveDefaultPeriodId(client.id, periods)) ?? '';
      set({ periods, periodId, periodsLoaded: true });
      void loadTaxonomy(client.id);
      if (periodId) {
        await load();
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  });

  /**
   * APT362: the saved taxonomy names the framework the way Report Settings does. Read once per client; when it cannot be read the screen
   * says what it always said (the framework code), so it never fails because of it.
   */
  const loadTaxonomy = async (clientId: string) => {
    try {
      const [settings, options] = await Promise.all([
        reportOptionsService.getReportSettings(clientId),
        reportOptionsService.getOptions().catch(() => null),
      ]);
      if (!alive.current) return;
      const code = settings?.taxonomy?.trim() || null;
      const listName = options?.taxonomies?.find((t) => t.code.trim().toUpperCase() === (code ?? '').toUpperCase())?.name ?? null;
      set({ taxonomyCode: code, taxonomyName: code ? taxonomyDisplayName(code, listName) : null });
    } catch {
      if (alive.current) set({ taxonomyCode: null, taxonomyName: null });
    }
  };

  /** Deep-links to the screen that answers the disclosure. */
  const fix = (field: StatutoryFieldRow) => {
    if (!field.fixRoute) {
      return;
    }
    navigate(fixUrl(field.fixRoute.replace('{field}', field.fieldCode)));
  };

  /** APT250 (review): the route of the Settings screen that answers the field, where its answer is changed. */
  const sourceRoute = (field: StatutoryFieldRow) => field.answerSourceRoute || field.fixRoute || null;

  const openSource = (field: StatutoryFieldRow) => {
    const route = sourceRoute(field);
    if (route) navigate(fixUrl(route.replace('{field}', field.fieldCode)));
  };

  /**
   * APT250 (review): overriding a state was allowed in practice- and approval-locked periods before APT250 widened
   * `isLocked`, and the override API still accepts it there; only a finalised or filed period stops it. An older API
   * without `canOverride` falls back to `!isLocked`.
   */
  const canOverride = () => (m.screen ? (m.screen.canOverride ?? !m.screen.isLocked) : false);

  /**
   * APT250 (review): bumped whenever the editor is opened or closed, so a section load or a save that finishes after the
   * preparer has moved on is recognised as stale and does not overwrite or close what is open now.
   */
  const answerToken = useRef(0);
  const amountInput = useRef<HTMLInputElement>(null);
  const overridePanel = useRef<HTMLDivElement>(null);
  // The override form is below the list: bring it into view when it opens.
  const overrideCode = m.overrideField?.fieldCode;
  useEffect(() => {
    if (overrideCode) overridePanel.current?.scrollIntoView?.({ block: 'nearest' });
  }, [overrideCode]);

  // ---- ap5-a6 (item 134) / APT250: answering a disclosure in its own row ----

  /**
   * True once a field's state means an answer is wanted here rather than on another screen. prod-p3: read the state
   * actually in force - `state` collapses every override to "Overridden".
   */
  const canAnswer = (field: StatutoryFieldRow) => !m.screen?.isLocked && field.effectiveState !== 0;

  const cancelAnswer = () => {
    answerToken.current += 1;
    set({ answerField: null, answerDefinition: null, answerSection: null, ...blankAnswer, isAnswerLoading: false });
  };

  const seedAnswerInputs = (definition: DisclosureFieldView) => {
    const value = definition.value;
    set({
      ...blankAnswer,
      answerText: value?.valueText ?? '',
      answerDecimal: value?.valueDecimal ?? null,
      answerAmountText: value?.valueDecimal === null || value?.valueDecimal === undefined ? '' : String(value.valueDecimal),
      answerDate: value?.valueDate ?? '',
      answerBoolean: value?.valueBoolean ?? null,
      answerChoice: value?.valueText ?? null,
    });
  };

  const openAnswer = async (field: StatutoryFieldRow) => {
    const token = ++answerToken.current;
    set({ answerField: field, answerDefinition: null, answerSection: null, isAnswerLoading: true, ...blankAnswer });

    // APT250: another row opened, Cancel pressed or this row reopened while this load ran - its result is not wanted.
    const stillOpen = () => alive.current && answerToken.current === token;
    try {
      const section = await phase2Service.getDisclosureSection(ref.current.periodId, field.sectionCode);
      if (!stillOpen()) return;
      const definition = (section.fields ?? []).find((f) => f.fieldId === field.fieldId) ?? null;
      set({ answerSection: section, answerDefinition: definition, isAnswerLoading: false });
      if (!definition) {
        notifyError('This disclosure is not editable here; use the Fix action to open the screen that sets it.');
        cancelAnswer();
        return;
      }
      seedAnswerInputs(definition);
    } catch (error) {
      if (!stillOpen()) return;
      notifyError(error);
      cancelAnswer();
    }
  };

  const buildAnswer = (definition: DisclosureFieldView): DisclosureAnswerInput | null => {
    const s = ref.current;
    // The API keys the answer by fieldId; fieldCode is rejected.
    const base: DisclosureAnswerInput = { fieldId: definition.fieldId, valueType: definition.valueType };
    switch (definition.valueType) {
      case 1:
        // APT250 (review): a box the browser cannot read as a number ('1,000' in some browsers) reports an empty value,
        // which used to save as "no amount" with "Answer saved.".
        if (
          s.answerAmountInvalid ||
          amountInput.current?.validity?.badInput ||
          (s.answerDecimal !== null && !Number.isFinite(s.answerDecimal))
        ) {
          notifyError(INVALID_AMOUNT);
          return null;
        }
        return { ...base, valueDecimal: s.answerDecimal ?? null };
      case 2:
        // APT250 (review): a date the field cannot read (31/04/2029) used to revert silently and re-save the old one.
        if (s.answerDateInvalid) {
          notifyError(INVALID_DATE);
          return null;
        }
        return { ...base, valueDate: s.answerDate?.trim() || null };
      case 3:
        if (s.answerBoolean === null) {
          notifyError('Choose Yes or No.');
          return null;
        }
        return { ...base, valueBoolean: s.answerBoolean };
      case 4:
        if (!s.answerChoice) {
          notifyError('Choose one of the listed options.');
          return null;
        }
        return { ...base, valueText: s.answerChoice };
      default:
        return { ...base, valueText: s.answerText?.trim() || null };
    }
  };

  const applyAnswer = async () => {
    const { answerField: field, answerDefinition: definition, answerSection: section, isAnswerSaving } = ref.current;
    if (!field || !definition || !section || isAnswerSaving) {
      return;
    }

    const answer = buildAnswer(definition);
    if (!answer) {
      return;
    }

    const token = answerToken.current;
    set({ isAnswerSaving: true });
    try {
      await phase2Service.saveDisclosureAnswers(ref.current.periodId, section.sectionCode, {
        expectedETag: section.eTag ?? null,
        answers: [answer],
      });
      if (!alive.current) return;
      notifySuccess('Answer saved.');
      // APT250 (review): close the editor only while it still shows the row that was saved - not one cancelled or
      // reopened while the save ran.
      if (answerToken.current === token) cancelAnswer();
      // The state of a disclosure follows its answer, so re-resolve the tree (the counts and the Answer column).
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ isAnswerSaving: false });
    }
  };

  /** APT250: Enter saves and Escape cancels in the answer's text and amount boxes. */
  const onAnswerKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void applyAnswer();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelAnswer();
    }
  };

  const openOverride = (field: StatutoryFieldRow) => {
    set({
      overrideField: field,
      overrideState: field.override?.state ?? 1,
      overrideReason: field.override?.reason ?? '',
      overrideTried: false,
    });
  };

  const cancelOverride = () => {
    set({ overrideField: null, overrideReason: '', overrideTried: false });
  };

  const saveOverride = async (fieldCode: string, state: number | null, reason: string | null) => {
    if (ref.current.isOverrideSaving) {
      return;
    }
    set({ isOverrideSaving: true });
    try {
      const screen = await phase2Service.overrideDisclosure({
        accountingPeriodId: ref.current.periodId,
        fieldCode,
        state,
        reason,
      });
      if (!alive.current) return;
      bind(screen);
      cancelOverride();
      notifySuccess(
        state === null
          ? 'The override has been removed and the disclosures revalidated.'
          : 'The override has been recorded and the disclosures revalidated.',
      );
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ isOverrideSaving: false });
    }
  };

  const applyOverride = () => {
    const s = ref.current;
    if (!s.overrideField || s.isOverrideSaving) {
      return;
    }
    if (!s.overrideReason.trim()) {
      set({ overrideTried: true });
      notifyError('Enter the reason for the override.');
      return;
    }

    void saveOverride(s.overrideField.fieldCode, s.overrideState, s.overrideReason.trim());
  };

  const clearOverride = (field: StatutoryFieldRow) => {
    void saveOverride(field.fieldCode, null, null);
  };

  /** APT250: the control that fits the field's value type (0 Text, 1 Decimal, 2 Date, 3 Yes/No, 4 Choice). */
  const answerControl = (definition: DisclosureFieldView): ReactNode => {
    switch (definition.valueType) {
      case 1:
        return (
          <InputField
            ref={amountInput}
            fieldClassName="w-200"
            charMode="number"
            allowDecimal
            allowNegative
            maxIntDigits={15}
            maxDecimals={4}
            placeholder="Amount"
            aria-label={definition.label}
            autoFocus
            invalid={m.answerAmountInvalid}
            value={m.answerAmountText}
            onChange={(event) => {
              const text = event.target.value;
              const number = numberFromInput(text);
              // A lone "-" or "." is not a number yet: refuse it on Save like any unreadable amount.
              const bad = !!event.target.validity?.badInput || (number !== null && !Number.isFinite(number));
              set({ answerAmountText: text, answerDecimal: bad ? null : number, answerAmountInvalid: bad });
            }}
            onKeyDown={onAnswerKey}
          />
        );
      case 2:
        return (
          <DateField
            fieldClassName="w-200"
            placeholder="dd/mm/yyyy"
            ariaLabel={definition.label}
            clearable
            invalid={!!m.answerDateInvalid}
            value={m.answerDate || null}
            onChange={(date) => set({ answerDate: date ? date.format('YYYY-MM-DD') : '' })}
            onInvalidText={(text) => set({ answerDateInvalid: text })}
          />
        );
      case 3:
        return (
          <Select
            className="w-200"
            placeholder="Choose Yes or No"
            aria-label={definition.label}
            items={YES_NO}
            bindLabel="name"
            bindValue="id"
            clearable={false}
            searchable={false}
            value={m.answerBoolean === null ? null : m.answerBoolean ? 'yes' : 'no'}
            onChange={(value) => set({ answerBoolean: value === 'yes' ? true : value === 'no' ? false : null })}
          />
        );
      case 4:
        return (
          <Select
            className="w-300"
            placeholder="Choose an option"
            aria-label={definition.label}
            items={definition.choices}
            bindLabel="label"
            bindValue="value"
            clearable={false}
            value={m.answerChoice}
            onChange={(value) => set({ answerChoice: (value as string | null) ?? null })}
          />
        );
      default:
        return (
          <InputField
            fieldClassName="w-300"
            placeholder="Answer"
            aria-label={definition.label}
            autoFocus
            maxLength={2000}
            value={m.answerText}
            onChange={(event) => set({ answerText: event.target.value })}
            onKeyDown={onAnswerKey}
          />
        );
    }
  };

  /** APT250: the Answer cell of the row being answered. */
  const answerEditor = () => {
    const definition = m.answerDefinition;
    return (
      <div className="d-flex flex-column gap-5 statutory-answer-editor">
        {m.isAnswerLoading && <span className="text-gray">Loading&hellip;</span>}
        {definition && answerControl(definition)}
        {definition?.helpText && <div className="text-gray font-size-12">{definition.helpText}</div>}
        {m.answerDateInvalid && <div className="text-red font-size-12">{INVALID_DATE}</div>}
        {m.answerAmountInvalid && <div className="text-red font-size-12">{INVALID_AMOUNT}</div>}
        <div className="d-flex align-items-center gap-10">
          {definition && (
            <Button
              className="action-button primary-button"
              busy={m.isAnswerSaving}
              onClick={() => void applyAnswer()}
            >
              Save
            </Button>
          )}
          <Button className="action-button secondary-button" onClick={cancelAnswer}>
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const columns: DataTableColumn<StatutoryFieldRow>[] = [
    {
      id: 'disclosure',
      header: 'Disclosure',
      cell: (field) => (
        <>
          {field.label}
          <div className="text-gray font-size-12">{field.fieldCode}</div>
        </>
      ),
    },
    {
      id: 'state',
      header: 'State',
      headerClassName: 'w-150',
      cell: (field) => (
        <>
          {/* ap5-a6 (item 133): the read procedure collapses any override to the single state "Overridden", which
              hid the state the reviewer actually chose. Show the chosen state and mark it as overridden instead. */}
          <div className={`d-flex align-items-center gap-5 ${stateClass(field)}`}>
            <Icon className="font-size-18" name={stateIcon(field)} outlined />
            <span>{field.override ? field.override.stateName : field.stateName}</span>
          </div>
          {field.override && <div className="text-gray font-size-12">Overridden</div>}
        </>
      ),
    },
    {
      id: 'rule',
      header: 'Rule',
      headerClassName: 'w-180',
      cell: (field) => (
        <>
          {field.ruleCode}
          <div className="text-gray font-size-12">{field.sourceReference}</div>
        </>
      ),
    },
    {
      id: 'why',
      header: 'Why required',
      cell: (field) => (
        <>
          {/* AP-008: the explanation ran several lines deep in every row; it now sits behind an info icon's
              tooltip, the same pattern the rest of the app uses for field hints (e.g. AddClient's Registration
              Number), so the row stays a glance-able single line unless the detail is asked for. */}
          <Tooltip title={field.whyRequired} placement="right">
            <Icon name="info" outlined className="font-size-16 text-primary statutory-why-icon" />
          </Tooltip>
          {field.trigger && <div className="text-gray font-size-12">Trigger: {field.trigger}</div>}
          {field.override && (
            <div className="text-gray font-size-12">
              Overridden to {field.override.stateName}: {field.override.reason}
            </div>
          )}
        </>
      ),
    },
    {
      id: 'answer',
      header: 'Answer',
      headerClassName: 'w-250',
      cell: (field) =>
        m.answerField?.fieldId === field.fieldId ? (
          answerEditor()
        ) : (
          <div className="d-flex align-items-center justify-space-between gap-10">
            <div className="statutory-answer">
              <span>{shownAnswer(field)}</span>
              {/* APT250: the Settings screen the answer comes from - the one the accounts print. */}
              {field.answerSource && <div className="text-gray font-size-12">From {field.answerSource}</div>}
              {/* APT250 (review): an answer recorded here that the accounts do not print is said, not shown. AP-008:
                  the full sentence used to run several lines in every differing row, the same clutter the Why
                  required column had; it now sits behind a tooltip too, leaving just a glance-able flag. */}
              {field.answerNote && (
                <Tooltip title={field.answerNote}>
                  <div className="d-flex align-items-center gap-5 text-red font-size-12 statutory-answer-note">
                    <Icon name="warning_amber" outlined className="font-size-16 text-red" />
                    Differs from source
                  </div>
                </Tooltip>
              )}
            </div>
            {/* ap5-a6 (item 134) / APT250: answer the disclosure here. The button used to be an edit icon that only
                appeared while the pointer was over the row, so the Answer column read as "-" with nothing to
                click; it is now always shown. APT250 (review): a disclosure a Settings screen answers is changed on
                that screen, so its link opens it rather than an editor here. */}
            {canAnswer(field) && field.answerSource && sourceRoute(field) && (
              <Button
                className="link statutory-answer-link"
                title={`Change this on ${field.answerSource}`}
                aria-label={`Change ${field.label}`}
                onClick={() => openSource(field)}
              >
                Change
              </Button>
            )}
            {canAnswer(field) && !field.answerSource && (
              <Button
                className="link statutory-answer-link"
                title="Answer this disclosure"
                aria-label={`${hasOwnAnswer(field) ? 'Change' : 'Answer'} ${field.label}`}
                // APT250 (review): one answer is saved at a time; opening another row mid-save raced the save.
                disabled={m.isAnswerSaving}
                onClick={() => void openAnswer(field)}
              >
                {hasOwnAnswer(field) ? 'Change' : 'Answer'}
              </Button>
            )}
          </div>
        ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (field) =>
        m.answerField?.fieldId === field.fieldId ? null : (
          <div className="d-flex align-items-center grid-actions">
            {field.fixRoute && (
              <Button title="Open the screen that answers this disclosure" onClick={() => fix(field)}>
                <Icon name="build" outlined />
              </Button>
            )}
            {field.allowOverride && canOverride() && (
              <Button title="Override the state" onClick={() => openOverride(field)}>
                <Icon name="gavel" outlined />
              </Button>
            )}
            {field.override && canOverride() && (
              <Button title="Remove override" onClick={() => clearOverride(field)}>
                <Icon name="undo" outlined />
              </Button>
            )}
          </div>
        ),
    },
  ];

  return (
    <SideMenuPage menu="task">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <SearchField
              placeholder="Search field, label, rule or reference"
              value={m.searchText}
              onValueChange={(searchText) => {
                set({ searchText });
                onSearchChange();
              }}
            />
          </div>

          <div className="content-header-right mb-1">
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              items={STATUTORY_STATES}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.stateFilter}
              onChange={(value) => {
                set({ stateFilter: value as number });
                void load();
              }}
            />
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder={NO_PERIOD_PLACEHOLDER}
              items={m.periods}
              bindLabel="fromDate"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.periodId || null}
              onChange={(value) => {
                // APT250: an answer being entered belongs to the period it was opened on.
                cancelAnswer();
                set({ periodId: value as string });
                rememberChosenPeriod(value as string);
                void load();
              }}
              renderLabel={periodTo}
              renderOption={periodTo}
            />
          </div>
        </div>
        <NoPeriodNote show={m.periodsLoaded && m.periods.length === 0} />

        {m.screen && (
          <div className="content-header pt-1 mb-0 hr-top">
            <div className="content-header-left mb-1">
              {/* ap5-a6 (item 135): this is the framework recorded against the selected accounting period (it is
                  NONSTAT for unincorporated clients, FRS102-1A for small companies), not a fixed default. */}
              {(() => {
                // APT362: the framework as the company's settings name it, with the rule set that applies beneath it.
                const framework = describeFramework({
                  frameworkCode: m.screen.frameworkCode,
                  accountsTypeCode: m.screen.accountsTypeCode,
                  taxonomyCode: m.taxonomyCode,
                  taxonomyName: m.taxonomyName,
                  lockedReason: m.screen.lockedReason ?? null,
                });
                return (
                  <>
                    <div className="text-gray font-size-14">{framework.title}</div>
                    {framework.detail && <div className="text-gray font-size-12">{framework.detail}</div>}
                  </>
                );
              })()}
            </div>
            <div className="content-header-right mb-1 d-flex align-items-center gap-15">
              {m.screen.counts.error > 0 && (
                <span className="text-red fw-bold">{m.screen.counts.error} unanswered</span>
              )}
              <span className="text-gray">{m.screen.counts.required} required</span>
              <span className="text-gray">{m.screen.counts.optional} optional</span>
              <span className="text-gray">{m.screen.counts.notApplicable} not applicable</span>
              {m.screen.counts.overridden > 0 && (
                <span className="text-gray">{m.screen.counts.overridden} overridden</span>
              )}
            </div>
          </div>
        )}

        {/* APT250: a locked period is read-only, and says why instead of just hiding the actions. */}
        {m.screen?.isLocked && (
          <div className="info-message primary-message mb-1 statutory-locked">
            <Icon name="lock" outlined />
            <span>{m.screen.lockedReason || LOCKED_FALLBACK}</span>
          </div>
        )}

        {m.sections.length > 0 && (
          <div className="statutory-section-tabs-wrap">
            <button
              type="button"
              className="statutory-tab-scroll"
              aria-label="Scroll sections left"
              disabled={!tabScroll.canLeft}
              onClick={() => scrollTabs(-240)}
            >
              <Icon name="chevron_left" outlined />
            </button>
            <div className="statutory-section-tabs" ref={tabStripRef} onScroll={updateTabScroll}>
              {m.sections.map((section) => (
                <button
                  key={section.sectionCode}
                  type="button"
                  className={`statutory-section-tab ${section.sectionCode === m.activeSection ? 'active' : ''}`}
                  onClick={() => selectSection(section.sectionCode)}
                >
                  {section.title}
                  {section.errorCount > 0 && <span className="statutory-tab-count">{section.errorCount}</span>}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="statutory-tab-scroll"
              aria-label="Scroll sections right"
              disabled={!tabScroll.canRight}
              onClick={() => scrollTabs(240)}
            >
              <Icon name="chevron_right" outlined />
            </button>
          </div>
        )}

        {(() => {
          const section = m.sections.find((s) => s.sectionCode === m.activeSection) ?? m.sections[0];
          if (!section) return null;
          return (
            <div className="mb-1">
              {section.errorCount > 0 && (
                <div className="pt-1 pb-5">
                  <span className="text-red font-size-14">{section.errorCount} unanswered</span>
                </div>
              )}
              <div className="mat-table-wrapper">
                <DataTable columns={columns} rows={section.fields} rowKey={(field) => field.fieldId} stickyHeader />
              </div>
            </div>
          );
        })()}

        {m.screen && m.sections.length === 0 && (
          <div className="info-message primary-message mb-1">
            No disclosures match the current search and state filter.
          </div>
        )}

        {/* The override form sits in the page, directly under the list: no fixed footer bar (2 Nov rule). */}
        {m.overrideField && (
          <div className="statutory-override-panel" ref={overridePanel}>
            <div className="d-flex align-items-center gap-15 flex-wrap">
              <div className="text-gray">{m.overrideField.label}</div>
              <Select
                className="w-150"
                items={OVERRIDE_STATES}
                bindLabel="name"
                bindValue="id"
                clearable={false}
                searchable={false}
                value={m.overrideState}
                onChange={(value) => set({ overrideState: value as number })}
              />
              <InputField
                fieldClassName="w-387"
                placeholder="Reason for the override"
                maxLength={500}
                invalid={m.overrideTried && !m.overrideReason.trim()}
                value={m.overrideReason}
                onChange={(event) => set({ overrideReason: event.target.value })}
              />
              <RequiredMark />
            </div>
            <div className="d-flex align-items-center gap-15 mt-1">
              <Button className="action-button primary-button" busy={m.isOverrideSaving} onClick={applyOverride}>
                Apply
              </Button>
              <Button className="action-button secondary-button" onClick={cancelOverride}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </SideMenuPage>
  );
}
